import { useState, useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { getAuthUrl, getCountries, joinCountry, leaveCountry } from '../utils/api';
import { getFlagImageUrl } from '../utils/flags';
import type { Country } from '../types';

// Flag image component with fallback - rectangular to show full flag respectfully
function FlagImage({ code, size = 20, style }: { code: string; size?: number; style?: CSSProperties }) {
  const [error, setError] = useState(false);
  const flagWidth = size * 1.5; // 3:2 aspect ratio
  const flagHeight = size;
  
  if (error) {
    return (
      <span style={{ fontSize: size * 0.5, fontWeight: 600, color: 'white', ...style }}>
        {code}
      </span>
    );
  }
  
  return (
    <img 
      src={getFlagImageUrl(code)} 
      alt={code}
      onError={() => setError(true)}
      style={{ 
        width: flagWidth, 
        height: flagHeight, 
        objectFit: 'cover', 
        borderRadius: '3px',
        ...style 
      }}
    />
  );
}

interface AuthButtonProps {
  onProfileClick: () => void;
  onAdminClick?: () => void;
  onCountryChange?: () => void;
  onLeaderboardClick?: () => void;
}

export default function AuthButton({ onProfileClick, onAdminClick, onCountryChange, onLeaderboardClick }: AuthButtonProps) {
  const { user, isAuthenticated, isLoading, logout, refreshUser } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [countryLoading, setCountryLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Fetch countries
  const { data: countriesData } = useQuery({
    queryKey: ['countries'],
    queryFn: getCountries,
    staleTime: 5 * 60 * 1000,
  });

  const countries = countriesData?.countries || [];
  
  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return countries.slice(0, 10); // Show first 10 by default
    const search = countrySearch.toLowerCase();
    return countries.filter(c => 
      c.name.toLowerCase().includes(search) || 
      c.code.toLowerCase().includes(search)
    ).slice(0, 10);
  }, [countries, countrySearch]);

  // Get user's current country
  const userCountry = useMemo(() => {
    if (!user?.countryCode) return null;
    return countries.find(c => c.code === user.countryCode) || null;
  }, [user?.countryCode, countries]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleCountrySelect = async (country: Country) => {
    try {
      setCountryLoading(true);
      await joinCountry(country.code);
      await refreshUser();
      setShowCountryPicker(false);
      setCountrySearch('');
      onCountryChange?.();
    } catch (error) {
      console.error('Failed to set country:', error);
    } finally {
      setCountryLoading(false);
    }
  };

  const handleCountryRemove = async () => {
    try {
      setCountryLoading(true);
      await leaveCountry();
      await refreshUser();
      onCountryChange?.();
    } catch (error) {
      console.error('Failed to remove country:', error);
    } finally {
      setCountryLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      setAuthLoading(true);
      const { url } = await getAuthUrl();
      // Use TWA-aware OAuth opening to keep flow within TWA
      const { openOAuthUrl } = await import('../utils/twa');
      openOAuthUrl(url);
    } catch (error) {
      console.error('Failed to get auth URL:', error);
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    setShowDropdown(false);
  };

  const loadingStyle: CSSProperties = {
    width: isMobile ? '36px' : '40px',
    height: isMobile ? '36px' : '40px',
    borderRadius: '10px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const signInButtonStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '6px' : '10px',
    padding: isMobile ? '8px 12px' : '10px 16px',
    borderRadius: isMobile ? '10px' : '12px',
    background: 'white',
    color: 'black',
    fontWeight: 600,
    fontSize: isMobile ? '13px' : '14px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(255,255,255,0.1)',
  };

  const userButtonStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '8px' : '10px',
    padding: isMobile ? '6px 10px' : '8px 12px',
    borderRadius: isMobile ? '10px' : '12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const dropdownStyle: CSSProperties = {
    position: 'absolute',
    right: 0,
    marginTop: '8px',
    width: isMobile ? '200px' : '220px',
    background: 'rgba(10, 10, 20, 0.95)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '14px',
    overflow: 'hidden',
    zIndex: 50,
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
    padding: '6px',
  };

  const dropdownItemStyle: CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: isMobile ? '8px 10px' : '10px 12px',
    borderRadius: '10px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: 'background 0.2s',
    textAlign: 'left',
  };

  if (isLoading) {
    return (
      <div style={loadingStyle}>
        <div className="animate-spin" style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.2)', borderTopColor: '#14F195', borderRadius: '50%' }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <button onClick={handleSignIn} disabled={authLoading} style={{ ...signInButtonStyle, opacity: authLoading ? 0.5 : 1 }}>
        {authLoading ? (
          <div className="animate-spin" style={{ width: '16px', height: '16px', border: '2px solid rgba(0,0,0,0.2)', borderTopColor: 'black', borderRadius: '50%' }} />
        ) : (
          <svg style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        )}
        <span>{isMobile ? 'Sign in' : 'Sign in with X'}</span>
      </button>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setShowDropdown(!showDropdown)} style={userButtonStyle}>
        {user?.xAvatarUrl ? (
          <img src={user.xAvatarUrl} alt={user.xDisplayName} style={{ width: isMobile ? '24px' : '28px', height: isMobile ? '24px' : '28px', borderRadius: '8px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }} />
        ) : (
          <div style={{ width: isMobile ? '24px' : '28px', height: isMobile ? '24px' : '28px', borderRadius: '8px', background: 'linear-gradient(135deg, #14F195, #9945FF)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: isMobile ? '10px' : '12px' }}>
            {(user?.name || user?.xDisplayName || 'U').charAt(0).toUpperCase()}
          </div>
        )}
        {!isMobile && (
          <div style={{ textAlign: 'left' }}>
            <p style={{ color: 'white', fontWeight: 500, fontSize: '14px', lineHeight: 1.2 }}>{user?.name || user?.xDisplayName}</p>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', lineHeight: 1.2 }}>@{user?.xUsername}</p>
          </div>
        )}
        <svg style={{ width: '14px', height: '14px', color: 'rgba(255,255,255,0.4)', transform: showDropdown ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', marginLeft: isMobile ? '0' : '4px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showDropdown && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setShowDropdown(false)} />
          <div style={dropdownStyle} className="animate-fade-in">
            {/* User info on mobile */}
            {isMobile && user && (
              <div style={{ padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: '6px' }}>
                <p style={{ color: 'white', fontWeight: 600, fontSize: '14px' }}>{user.name || user.xDisplayName}</p>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>@{user.xUsername}</p>
              </div>
            )}
            
            <button
              onClick={() => { onProfileClick(); setShowDropdown(false); }}
              style={dropdownItemStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg style={{ width: '16px', height: '16px', color: 'rgba(255,255,255,0.6)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: 'white' }}>Edit Profile</div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>Update your info</div>
              </div>
            </button>

            {/* Country Selector */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowCountryPicker(!showCountryPicker)}
                style={dropdownItemStyle}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(20,241,149,0.05)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '8px', 
                  background: userCountry ? 'rgba(20,241,149,0.15)' : 'rgba(255,255,255,0.05)', 
                  border: `1px solid ${userCountry ? 'rgba(20,241,149,0.3)' : 'rgba(255,255,255,0.1)'}`, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  flexShrink: 0,
                  overflow: 'hidden',
                }}>
                  {userCountry ? <FlagImage code={userCountry.code} size={28} /> : <span style={{ fontSize: '18px' }}>🌍</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: userCountry ? '#14F195' : 'white' }}>
                    {userCountry ? userCountry.name : 'Your Country'}
                  </div>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>
                    {userCountry ? 'Representing' : 'Select your country'}
                  </div>
                </div>
                <svg style={{ width: '14px', height: '14px', color: 'rgba(255,255,255,0.4)', transform: showCountryPicker ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showCountryPicker && (
                <div style={{
                  position: 'absolute',
                  left: '-8px',
                  right: '-8px',
                  top: '100%',
                  marginTop: '4px',
                  background: 'rgba(20, 20, 30, 0.98)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  zIndex: 60,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                }}>
                  <div style={{ padding: '8px' }}>
                    <input
                      type="text"
                      placeholder="Search countries..."
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.1)',
                        background: 'rgba(255,255,255,0.05)',
                        color: 'white',
                        fontSize: '13px',
                        outline: 'none',
                      }}
                      autoFocus
                    />
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '0 4px 4px' }}>
                    {filteredCountries.map((country) => (
                      <button
                        key={country.code}
                        onClick={() => handleCountrySelect(country)}
                        disabled={countryLoading}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          border: 'none',
                          background: user?.countryCode === country.code ? 'rgba(20,241,149,0.15)' : 'transparent',
                          cursor: countryLoading ? 'not-allowed' : 'pointer',
                          opacity: countryLoading ? 0.5 : 1,
                          textAlign: 'left',
                        }}
                        onMouseEnter={(e) => { if (user?.countryCode !== country.code) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = user?.countryCode === country.code ? 'rgba(20,241,149,0.15)' : 'transparent'; }}
                      >
                        <FlagImage code={country.code} size={20} />
                        <span style={{ fontSize: '13px', color: 'white', flex: 1 }}>{country.name}</span>
                        {user?.countryCode === country.code && (
                          <svg style={{ width: '14px', height: '14px', color: '#14F195' }} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                          </svg>
                        )}
                      </button>
                    ))}
                    {filteredCountries.length === 0 && (
                      <div style={{ padding: '16px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>No countries found</div>
                    )}
                  </div>
                  {userCountry && (
                    <>
                      <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '4px 8px' }} />
                      <div style={{ padding: '4px' }}>
                        <button
                          onClick={handleCountryRemove}
                          disabled={countryLoading}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '8px',
                            border: 'none',
                            background: 'transparent',
                            color: '#ef4444',
                            fontSize: '12px',
                            fontWeight: 500,
                            cursor: countryLoading ? 'not-allowed' : 'pointer',
                            opacity: countryLoading ? 0.5 : 1,
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          Remove country
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            
            {/* Leaderboard Button */}
            {onLeaderboardClick && (
              <button
                onClick={() => { onLeaderboardClick(); setShowDropdown(false); }}
                style={dropdownItemStyle}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: '18px' }}>🏆</span>
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: 'white' }}>Leaderboard</div>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>View rankings</div>
                </div>
              </button>
            )}

            {/* Admin Panel - only show if user is admin */}
            {user?.isAdmin && onAdminClick && (
              <button
                onClick={() => { onAdminClick(); setShowDropdown(false); }}
                style={dropdownItemStyle}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(153,69,255,0.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(153,69,255,0.15)', border: '1px solid rgba(153,69,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg style={{ width: '16px', height: '16px', color: '#9945FF' }} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#9945FF' }}>Admin Panel</div>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>Manage team leads</div>
                </div>
              </button>
            )}
            
            <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)', margin: '6px 8px' }} />
            
            <button
              onClick={handleLogout}
              style={dropdownItemStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg style={{ width: '16px', height: '16px', color: 'rgba(255,255,255,0.6)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: 'white' }}>Sign Out</div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>See you soon</div>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
